import { ReactNode } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Download, FileText, Loader2, Shield } from "lucide-react";
import type { AIResults } from "./ProposalResults";
import type { ProposalFormData } from "./ProposalForm";
import type { StaffingLineItem } from "./StaffingTable";
import { calculateStaffingTotals, calculateLineItemWeekly, totalOfficerCount } from "./StaffingTable";
import ProposalResults from "./ProposalResults";
import RefinePanel from "./RefinePanel";
import RefineDiffSummary, { type RefineDiffSection } from "./RefineDiffSummary";

interface SecurityGenerateStepProps {
  formData: ProposalFormData | null;
  staffingItems: StaffingLineItem[];
  results: AIResults | null;
  /** Previous AI results to diff against after a refinement (null = no diff to show) */
  previousResults?: AIResults | null;
  isLoading: boolean;
  onGenerate: (additionalContext?: string) => void;
  onExportPDF: () => void;
  onResultsChange: (results: AIResults) => void;
  extraContent?: ReactNode;
}

const fmt = (n: number) =>
  n.toLocaleString("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 0 });

const PROPOSAL_DIFF_SECTIONS: { key: keyof AIResults; label: string }[] = [
  { key: "executiveSummary", label: "Executive Summary" },
  { key: "threatAssessment", label: "Threat Assessment" },
  { key: "riskImpact", label: "Risk Level & Operational Impact" },
  { key: "staffingRecommendation", label: "Recommended Security Staffing" },
  { key: "securityActions", label: "Recommended Security Actions" },
  { key: "crimeDataSummary", label: "Crime Data Analysis" },
  { key: "implementationPlan", label: "Implementation Plan" },
  { key: "expectedOutcomes", label: "Expected Outcomes" },
];

const buildProposalDiff = (prev: AIResults, next: AIResults): RefineDiffSection[] => {
  const sections: RefineDiffSection[] = PROPOSAL_DIFF_SECTIONS.map(({ key, label }) => ({
    key: String(key),
    label,
    before: (prev[key] as string) || "",
    after: (next[key] as string) || "",
  }));
  if (prev.riskLevel !== next.riskLevel) {
    sections.unshift({
      key: "riskLevel",
      label: "Overall Risk Level",
      before: prev.riskLevel || "",
      after: next.riskLevel || "",
    });
  }
  return sections;
};

const SecurityGenerateStep = ({
  formData,
  staffingItems,
  results,
  previousResults,
  isLoading,
  onGenerate,
  onExportPDF,
  onResultsChange,
  extraContent,
}: SecurityGenerateStepProps) => {
  const totals = calculateStaffingTotals(staffingItems);
  const personnelCount = totalOfficerCount(staffingItems);

  if (results) {
    const diffSections = previousResults ? buildProposalDiff(previousResults, results) : null;
    return (
      <div className="space-y-6">
        <div className="flex gap-3 mb-6">
          <Button onClick={onExportPDF} className="gradient-primary text-primary-foreground shadow-glow hover:opacity-90">
            <Download className="mr-2 h-4 w-4" /> Export PDF
          </Button>
          <p className="text-xs text-muted-foreground self-center">Edit any section before exporting</p>
        </div>
        {diffSections && <RefineDiffSummary sections={diffSections} />}
        <ProposalResults results={results} isLoading={false} onResultsChange={onResultsChange} />
        {extraContent && <div className="mt-6">{extraContent}</div>}
        <RefinePanel
          isLoading={isLoading}
          onRegenerate={(ctx) => onGenerate(ctx)}
          title="Add More Details & Regenerate Proposal"
          description="The AI may have surfaced angles you hadn't considered. Add any missing context, corrections, or new requirements below to produce a refined proposal."
          placeholder={"Examples:\n• Client also needs a roving patrol vehicle on weekends\n• Site has unstaffed loading dock after 8pm — please address\n• Two recent break-in attempts at neighboring property\n• Add post orders for armored car deliveries on Fridays"}
        />
      </div>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Shield className="h-5 w-5 text-primary" />
          Ready to Generate Security Proposal
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {formData ? (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div>
                <p className="font-medium text-muted-foreground">Location:</p>
                <p>{formData.address}, {formData.city}, {formData.state} {formData.zip}</p>
              </div>
              <div>
                <p className="font-medium text-muted-foreground">Property Type:</p>
                <p className="capitalize">{formData.propertyType?.replace(/_/g, " ")}</p>
              </div>
              <div>
                <p className="font-medium text-muted-foreground">Service:</p>
                <p className="capitalize">{formData.serviceType?.replace(/_/g, " ")}</p>
              </div>
              <div>
                <p className="font-medium text-muted-foreground">Total Personnel:</p>
                <p>{personnelCount}</p>
              </div>
            </div>

            {/* Staffing breakdown */}
            <div className="rounded-lg border border-border bg-secondary/50 p-4 space-y-2">
              <p className="font-medium text-sm mb-2">Staffing Breakdown</p>
              {staffingItems.map((item) => {
                const clearance = item.clearanceLevel && item.clearanceLevel !== "none" ? ` [${item.clearanceLevel.replace(/_/g, " ").toUpperCase()}]` : "";
                return (
                  <div key={item.id} className="flex justify-between text-sm">
                    <span className="text-muted-foreground">
                      {item.quantity} {item.armedStatus} {item.roleType}(s){clearance} — ${item.payRate}/hr — {item.hoursPerDay}hrs — {item.daysPerWeek}days
                    </span>
                    <span className="font-medium text-foreground">
                      {fmt(calculateLineItemWeekly(item))}/wk
                    </span>
                  </div>
                );
              })}
            </div>

            <div className="p-4 bg-secondary/50 rounded-lg">
              <p className="font-medium mb-2">Total Cost Estimate</p>
              <div className="text-2xl font-bold text-primary">
                {fmt(totals.weekly)}/wk
              </div>
              <p className="text-sm text-muted-foreground">
                {fmt(totals.monthly)}/mo · {fmt(totals.yearly)}/yr
              </p>
            </div>

            <div className="flex justify-center">
              <Button
                onClick={() => onGenerate()}
                disabled={isLoading}
                size="lg"
                className="gradient-primary text-primary-foreground shadow-glow hover:opacity-90"
              >
                {isLoading ? (
                  <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Generating Proposal...</>
                ) : (
                  <><FileText className="mr-2 h-4 w-4" /> Generate Security Proposal</>
                )}
              </Button>
            </div>
          </>
        ) : (
          <p className="text-muted-foreground text-center py-8">
            Please fill in the property and service details first.
          </p>
        )}
      </CardContent>
    </Card>
  );
};

export default SecurityGenerateStep;
